/**
 * Archetypes — named MOVEMENT models, the size up from a Form.
 *
 * The engine's thesis is "feel is data": a Form is a `Partial<PhysicsConfig>` merged
 * over a base, and a Mutator is the same overlay shape. An archetype is the next size
 * up — a named bundle of (a) a PhysicsConfig PROFILE, (b) the movement VERBS it enables
 * (wall-slide / wall-jump), and (c) its interaction RULES (does the player have
 * offense?). It is resolved at load like a mutator set; its physics profile is merged
 * over the resolved form config (the SAME mergeConfig mechanism), and Player gates the
 * verb code on the verb flags so a non-PRECISION form never touches wall code and stays
 * bit-identical to today.
 *
 * Authored-in (Isaac, 2026-06-30): `meta.archetype` is fixed per level; the game plays a
 * level under its authored archetype. It is EXCLUDED from computeLevelId (it lives in
 * meta), so a Precision run and a Classic run of the SAME geometry would otherwise
 * collide on one board — the run/board key therefore carries the archetype alongside
 * mutatorSetId (see Replay / PbStore / the runs board key).
 *
 * Determinism: integer-only id work (the same FNV-1a-free string compare as a known-id
 * lookup); the only floats are the profile numbers (exact merge under IEEE-754), so this
 * obeys the src/engine no-transcendentals lint.
 */
import { type PhysicsConfig } from './PhysicsConfig';

/**
 * The IMPLEMENTED archetypes (the editor dropdown + sanitize iterate this). The stored
 * `meta.archetype` is a permissive string so the format can forward-carry an id that
 * isn't built yet (the handoff names a future 'combat'); an unknown id DEGRADES to
 * 'classic' at resolve time, never traps. Add 'combat' here + a registry entry when it
 * ships. ORDER here is the canonical UI order.
 */
export const ARCHETYPE_IDS = ['classic', 'precision', 'combat', 'dashprecision', 'momentum', 'autorunner', 'runandgun'] as const;

export type ArchetypeId = (typeof ARCHETYPE_IDS)[number];

/** The default when a level omits `meta.archetype` (legacy / un-authored → today's feel). */
export const DEFAULT_ARCHETYPE: ArchetypeId = 'classic';

/** Movement verbs an archetype may enable. Player reads these to gate the verb code. */
export interface ArchetypeVerbs {
  /** Passive wall-slide STATE (no input): cling + clamp fall speed when airborne beside a wall. */
  readonly wallSlide: boolean;
  /** Wall-jump: from the wall-slide state, jump launches away from the wall. */
  readonly wallJump: boolean;
  /** COMBAT: directional melee hitbox + the down-attack pogo bounce. */
  readonly attack: boolean;
  /** COMBAT / DASH-PRECISION: directional air dash (COMBAT = horizontal dash-KILL with a
   *  damage hitbox; DASH-PRECISION = 8-way charge dash, pure traversal — the kill is gated by
   *  `offense`, so the same verb is a weapon under COMBAT and a movement tool under DASH-PRECISION). */
  readonly dash: boolean;
  /** DASH-PRECISION: stamina-gated wall-climb (the `grab` grip). Off for every other archetype. */
  readonly climb: boolean;
  /** RUN-AND-GUN: player-fired projectile (the gun). Gates the fire trigger + aim in Player and
   *  the bullet's enemy-kill in the Sim. Off for every other archetype (the fire path never runs). */
  readonly shoot: boolean;
}

/** A named archetype: feel profile + enabled verbs + interaction rules. */
export interface ArchetypeDef {
  readonly id: ArchetypeId;
  readonly label: string;
  readonly blurb: string;
  /** Feel profile merged OVER the resolved form config (the form mechanism, reused). */
  readonly physics: Partial<PhysicsConfig>;
  readonly verbs: ArchetypeVerbs;
  /**
   * Can the player deal damage (stomp enemies)? CLASSIC = true (today's behaviour).
   * PRECISION = false → enemies + spikes are PURE HAZARDS you route around; a contact
   * that would have been a stomp instead costs a heart (World threads this to entities
   * via ctx.offenseEnabled). This is why `defeat_all_enemies` is incoherent under
   * PRECISION (no way to deal damage) — the editor disables that win-rule.
   */
  readonly offense: boolean;
}

/**
 * The registry. PRECISION numbers are DIRECTIONAL TARGETS (per the handoff), not final
 * tuned values — player-feel can't be auto-verified (the headless preview throttles
 * rAF), so live tuning via `npm run dev` is OWED to Isaac. Every value is a named
 * PhysicsConfig field so it stays data, not code.
 *
 * Note on the handoff's "turnFriction" split: the config ALREADY separates "decel when
 * releasing" (groundDecel) from "decel when reversing" (skidDecel), so no new field is
 * needed — PRECISION sets a very high groundDecel (near-instant stop on release) and a
 * MODERATE, distinctly-lower skidDecel (the short directional slide on reversal).
 */
export const ARCHETYPES: Record<ArchetypeId, ArchetypeDef> = {
  classic: {
    id: 'classic',
    label: 'Classic',
    blurb: 'The default platformer feel — momentum, stomp offense, no wall verbs.',
    physics: {}, // identity → today's feel, bit-for-bit
    verbs: { wallSlide: false, wallJump: false, attack: false, dash: false, climb: false, shoot: false },
    offense: true,
  },
  precision: {
    id: 'precision',
    label: 'Precision',
    blurb: 'Twitch speed-platformer (Super Meat Boy): near-total air control, passive wall-slide, central wall-jump. No player offense — enemies are pure hazards.',
    physics: {
      // Near-total mid-air authority. Feel-research (2026-06-30): Tommy Refenes (SMB's sole
      // physics programmer) states on the record that SMB air-reversal has FRICTION ("the
      // amount of time when you're going in one direction and then switch to another") — it is
      // NOT instant. So airControl drops just below 1.0 (ground stays instant-on/off; the air
      // keeps near-total but not total steering) and an air-brake gives the reversal a cost.
      // Both magnitudes are feel-tune OWED — SMB is closed-source with ZERO documented numbers.
      airControl: 0.9,     // was 1.0; direction sourced (must be < 1.0), exact value a feel call
      airBrakeDecel: 1400, // air-reversal friction (the Refenes "air friction"); reuses the §1 field
      coyoteTimeMs: 60,    // was BASE 90; SMB has NO coyote ("no edge roll-off" — Refenes). A
                           // deliberate playability compromise (not SMB's literal 0) — feel-tune.
      // Stopping is free (release → near-instant); reversing has friction (a short slide).
      groundAccel: 2600,   // snappy ramp-up (twitchy); airControl mirrors most of it into the air
      groundDecel: 5200,   // near-instant stop on release
      skidDecel: 1700,     // MODERATE reversal slide — distinctly lower than groundDecel
      skidThreshold: 20,   // a reversal counts as a skid-slide sooner
      // High top speed (speed is the fantasy AND the hazard).
      walkMaxSpeed: 300,
      runMaxSpeed: 520,
      // Heavier, faster falls than CLASSIC.
      gravity: 2700,
      jumpReleaseGravity: 5200, // keeps variable-height jump (hold = float), just heavier
      terminalVelocity: 1500,
      jumpVelocity: 760,
      // Wall verbs (active only because the precision verbs flags are on).
      wallSlideMaxFallSpeed: 260, // slow controlled cling-slide
      wallJumpPushX: 360,         // launch away from the wall (≈ run speed → clears gaps)
      wallJumpVelocityY: 760,     // up-and-out
      wallJumpControlLockSteps: 7, // ≈0.12 s so the kick leaves the wall before full air control
    },
    verbs: { wallSlide: true, wallJump: true, attack: false, dash: false, climb: false, shoot: false },
    offense: false,
  },
  combat: {
    id: 'combat',
    label: 'Combat',
    blurb: 'Action-platformer (Hollow Knight pogo + Sonic dash-kill): a directional sword, an automatic down-attack bounce, and an air dash-kill. Stomp stays on; spikes can be authored climbable (pogoable).',
    physics: {
      // Moderate mid-air authority — ABOVE Classic (0), BELOW Precision (1.0): enough to
      // AIM a pogo without trivialising positioning.
      airControl: 0.55,
      // Snappy ground feel, a touch quicker than base (not Precision-twitchy).
      groundAccel: 1800,
      groundDecel: 2400,
      walkMaxSpeed: 230,
      runMaxSpeed: 400,
      // Slightly heavier than base so the pogo bounce arc reads crisply.
      gravity: 2400,
      jumpReleaseGravity: 4600,
      terminalVelocity: 1300,
      jumpVelocity: 720,
      // ── Offense verbs (active only because the combat verb flags are on) ──
      // DIRECTIONAL TARGETS (per the handoff) — bounce-arc + knockback FEEL is OWED to
      // Isaac via `npm run dev` (the headless preview throttles rAF). All are named
      // PhysicsConfig fields so they stay data, live-tunable.
      pogoBounceVelocity: 820,    // the headline knob — an automatic, readable bounce
      attackHitboxActiveSteps: 8, // ≈0.13 s swing window
      attackHitboxReach: 46,      // px the hitbox extends past the body
      attackCooldownSteps: 12,    // ≈0.20 s between swings
      pogoKnockbackVelocity: 140, // small recoil opposite the strike
      dashVelocity: 720,          // a fast horizontal lunge
      dashSteps: 9,               // ≈0.15 s committed dash
      dashCooldownSteps: 24,      // ≈0.40 s between dashes (a connect refreshes to 0)
    },
    verbs: { wallSlide: false, wallJump: false, attack: true, dash: true, climb: false, shoot: false },
    // Stomp STAYS ON in COMBAT (Isaac, 2026-06-30): the sword/pogo is additive offense,
    // not a replacement. offenseEnabled = true → enemies stomp as in CLASSIC.
    offense: true,
  },
  dashprecision: {
    id: 'dashprecision',
    label: 'Dash',
    blurb: 'Puzzle-precision (Celeste): a single 8-way air-dash that refreshes on LAND (+ dash-refill gems), a stamina-gated wall-climb, wall-slide + wall-jump. NO player offense — enemies are pure hazards you route/dash around.',
    physics: {
      // DELIBERATE, not a speed gauntlet (per the research): moderate air authority — ABOVE
      // Classic (0), BELOW Precision (1.0) — and a LOWER top speed than Precision. The dash is
      // the expression, not raw speed. DIRECTIONAL TARGETS — feel-tune OWED to Isaac (`npm run dev`).
      airControl: 0.6,
      groundAccel: 2200,
      groundDecel: 3800,   // crisp stop (deliberate placement)
      skidDecel: 2000,
      skidThreshold: 25,
      walkMaxSpeed: 230,
      runMaxSpeed: 360,    // well below Precision's 520 — bursts, not a gauntlet
      gravity: 2500,
      jumpReleaseGravity: 4800,
      terminalVelocity: 1300,
      jumpVelocity: 720,
      // APEX-HANG (Celeste, feel-research 2026-06-30): half-gravity near the top of a held
      // jump — "the top of your jump has half gravity applied" (Maddy Thorson). A readable
      // float that is a big part of the Celeste feel. Engine-coherent values (kept in the
      // current band, NOT the ×8 tile-faithful port, to preserve the beatable climb fixture).
      apexHangThreshold: 250,  // |vy| px/s window the hang applies within — feel-tune OWED
      apexHangGravity: 1250,   // ≈ half of gravity (2500) inside the window
      // DASH-FREEZE (Celeste): a brief hitstop at the start of the 8-way charge dash — the
      // committal "punch" that makes the dash read as a discrete, weighty action. Celeste ≈ 3
      // frames (community-measured; the shipped-source value wasn't reachable) → feel-tune OWED.
      dashFreezeSteps: 3,
      // Wall verbs (cling + kick) — shared with PRECISION; climb sits on top of them.
      wallSlideMaxFallSpeed: 240,
      wallJumpPushX: 320,
      wallJumpVelocityY: 720,
      wallJumpControlLockSteps: 6,
      // Air-dash: the CHARGE model (single charge, refresh on land + a dashRefill pickup),
      // 8-way (the aim comes from axisX + aimUp/duck at the dash press). A snappy committed lunge.
      dashVelocity: 760,
      dashSteps: 9,        // ≈0.15 s committed dash
      dashMaxCharges: 1,   // > 0 → charge model (NOT cooldown); refills on land
      // Stamina wall-climb (the one new primitive): ~110 steps ≈ 1.8 s of climbing at 210 px/s
      // ≈ 5–6 tiles before the grip fails and the player drops to the wall-slide.
      climbStaminaMax: 110,
      climbDrainPerStep: 1,
      climbSpeed: 210,
    },
    verbs: { wallSlide: true, wallJump: true, attack: false, dash: true, climb: true, shoot: false },
    offense: false, // dash is PURE TRAVERSAL — the kill is gated off (World.resolveAttackHitbox)
  },
  momentum: {
    id: 'momentum',
    label: 'Momentum',
    blurb: 'Flow-momentum (N++): sticky, expensive-to-shed speed; alternating wall-jumps BUILD speed. The skill is managing momentum, not executing inputs. NO player offense. (Sonic-flow without slopes.)',
    physics: {
      // The INVERSE of Precision's instant-stop: partial (not total) air control + LOW decel
      // so momentum persists, HIGH top speed. The §1 air-brake gives a turnable-but-costly air
      // reversal (the combination Precision/Classic can't express). DIRECTIONAL TARGETS — OWED.
      airControl: 0.4,     // sticky, NOT the Precision instant-turn
      groundAccel: 1500,
      groundDecel: 650,    // LOW → coasts a long time on the ground
      airAccel: 700,
      airDecel: 220,       // LOW air-coast (the flat fallback; replaced by airDragMultiplier on a coast)
      airBrakeDecel: 1500, // MODERATE air-brake → you CAN turn mid-air, it just costs (the §1 split)
      airDragMultiplier: 0.99, // feel-research: N++ air friction is MULTIPLICATIVE (vx*=k/frame) —
                               //   speed-proportional glide that flat decel can't reproduce. Coast-only
                               //   (a held direction is unaffected → never fights the additive build).
      skidDecel: 900,
      skidThreshold: 40,
      walkMaxSpeed: 300,
      runMaxSpeed: 620,    // HIGH top speed (speed is the reward AND the hazard)
      // Feel-research: N's held:released gravity ratio is ~6:1 ("long hang held, sharp drop on
      // release"); the old 2200/4200 was only ~1.9:1. Widened toward it (a moderate step, not the
      // full 6:1 — feel-tune OWED): lighter hold-gravity + a much heavier release cut.
      gravity: 2000,
      jumpReleaseGravity: 7000,
      terminalVelocity: 1450,
      jumpVelocity: 740,
      // Wall verbs — ADDITIVE wall-jump so alternating-wall kicks build speed (the N++ signature).
      wallSlideMaxFallSpeed: 300,
      wallJumpPushX: 300,
      wallJumpVelocityY: 720,
      wallJumpControlLockSteps: 6,
      wallJumpAdditive: true,
      wallJumpMaxSpeedX: 1240, // cap the accumulated horizontal speed (≈ 2× runMaxSpeed)
    },
    verbs: { wallSlide: true, wallJump: true, attack: false, dash: false, climb: false, shoot: false },
    offense: false,
  },
  autorunner: {
    id: 'autorunner',
    label: 'Auto-Run',
    blurb: 'One-thumb auto-runner (Canabalt / Meat Boy Forever): you cruise forward automatically — JUMP is the verb, hold DUCK in the air to DIVE through gaps, and a wall-jump FLIPS your run direction (the turn). NO player offense — enemies are pure hazards.',
    physics: {
      // AUTO-RUN: the constant cruise speed (autoRunSpeed > 0 = the mode ON, axisX ignored).
      // DIRECTIONAL TARGETS (per the handoff) — the cruise / dive / turn FEEL is OWED to Isaac
      // via `npm run dev`. Every value is a named PhysicsConfig field so it stays data.
      autoRunSpeed: 360,         // brisk constant cruise (≈ base runMaxSpeed)
      groundAccel: 2200,         // ramp to cruise off spawn / a wall-jump (not an instant snap)
      airAccel: 1400,            // hold the cruise through the air
      // Jump is the PRIMARY verb — a generous variable-height arc (the whole game is jump timing).
      jumpVelocity: 760,
      gravity: 2300,
      jumpReleaseGravity: 4400,
      terminalVelocity: 1350,
      // DIVE (hold duck in the air): a strong pull to a high cap for threading gaps. diveSpeed
      // > terminalVelocity so the dive beats the normal clamp; diveGravity ≈ 2.3× gravity so it
      // reaches diveSpeed faster than free-fall.
      diveSpeed: 1500,
      diveGravity: 5200,
      // Wall verbs = the TURN mechanic: cling beside a wall, then a wall-jump FLIPS autoRunDir (§4).
      wallSlideMaxFallSpeed: 280,
      wallJumpPushX: 360,        // launch ≈ cruise speed so the turn carries cleanly
      wallJumpVelocityY: 760,
      wallJumpControlLockSteps: 7,
    },
    verbs: { wallSlide: true, wallJump: true, attack: false, dash: false, climb: false, shoot: false },
    offense: false,
  },
  runandgun: {
    id: 'runandgun',
    label: 'Run & Gun',
    blurb: 'Big combat-forward shooter (Contra / Metal Slug / Gunstar): a player-fired bullet is the whole identity — hold FIRE to spray, aim 8-way with move + ↑/duck. NO body-stomp (enemies are hazards to your body); your BULLETS are the only kill. Free horizontal movement.',
    physics: {
      // Moderate mid-air authority — ABOVE Classic (0), enough to reposition + aim while jumping,
      // BELOW the Precision instant-turn. DIRECTIONAL TARGETS (per the handoff §7) — the fire rate,
      // bullet speed, aim, and movement FEEL are OWED to Isaac via `npm run dev`. Every value is a
      // named PhysicsConfig field so it stays data, live-tunable.
      airControl: 0.5,
      groundAccel: 1700,
      groundDecel: 2200,
      walkMaxSpeed: 220,
      runMaxSpeed: 380,
      gravity: 2200,
      jumpReleaseGravity: 4400,
      terminalVelocity: 1300,
      // 720 apex'd at ~118px < 128px (2 tiles) → couldn't change levels (playtest, 2026-07-01).
      // 860 apex's at ~168px (~2.6 tiles) — clears a 2-tile ledge with margin. Feel-tune OWED.
      jumpVelocity: 860,
      // ── The gun (active only because verbs.shoot is on) ──
      // The projectile primitive's knobs. projectileSpeed > 0 turns the gun ON; the fire cadence
      // (shootCooldownSteps) caps the held auto-fire spray rate; projectileLifeSteps despawns a
      // missed shot so it never leaks. DIRECTIONAL TARGETS — feel OWED.
      projectileSpeed: 900,      // brisk bullet travel (px/s)
      projectileLifeSteps: 90,   // ≈1.5 s before a missed shot despawns (deterministic timeout)
      shootCooldownSteps: 7,     // ≈8.5 shots/s while FIRE is held (the auto-fire rate cap)
    },
    verbs: { wallSlide: false, wallJump: false, attack: false, dash: false, climb: false, shoot: true },
    // BULLETS-ONLY offense (Isaac, 2026-06-30): no body-stomp — faithful to Contra. The bullet kill
    // is gated on the `shoot` verb (via Sim.allowEnemyKills = offense || verbs.shoot), NOT on
    // offenseEnabled, so a stomp still needs offense but a bullet needs the gun.
    offense: false,
  },
};

/** Coerce any stored value to a KNOWN archetype id; unknown / absent ⇒ the default. */
export function sanitizeArchetype(raw: unknown): ArchetypeId {
  return typeof raw === 'string' && (ARCHETYPE_IDS as readonly string[]).includes(raw)
    ? (raw as ArchetypeId)
    : DEFAULT_ARCHETYPE;
}

/** Resolve any stored value into its archetype rules (degrading unknown → classic). */
export function resolveArchetype(raw: unknown): ArchetypeDef {
  return ARCHETYPES[sanitizeArchetype(raw)];
}

/** Human-readable label for the HUD / leaderboard category (e.g. "Precision"). */
export function archetypeLabel(raw: unknown): string {
  return resolveArchetype(raw).label;
}

/**
 * A short controls hint for an archetype's EXTRA verbs (the keys beyond move/jump/wall), for the
 * HUD + the editor-playtest banner so a level's special keys are DISCOVERABLE (the gap that made
 * COMBAT's keys invisible in the editor playtest). '' when the archetype adds no new keys
 * (classic/precision/momentum share the base move/jump/wall keys).
 */
export function archetypeControlsHint(raw: unknown): string {
  switch (sanitizeArchetype(raw)) {
    case 'combat': return 'attack J · dash U · pogo = duck + J in the air';
    case 'dashprecision': return 'dash U (8-way: aim with move + ↑/duck) · grip + climb C';
    case 'autorunner': return 'auto-run — jump W/Space (hold = higher) · dive = hold S in the air · wall-jump flips your direction';
    case 'runandgun': return 'run & gun — hold FIRE J to shoot (aim with move + ↑/duck) · jump W/Space';
    default: return '';
  }
}

/**
 * Is this archetype a non-default board CATEGORY? Vanilla CLASSIC shares the bare
 * (levelId, mutatorSetId) board for back-compat; a non-classic archetype suffixes the
 * key (mirrors the mutatorSetId vanilla-sentinel rule).
 */
export function isDefaultArchetype(raw: unknown): boolean {
  return sanitizeArchetype(raw) === DEFAULT_ARCHETYPE;
}
